import type { CycleCountMetrics } from '../lib/metricsTypes';

// Per-run metrics view rendered when the Cycle Count run ends. Uses a
// definition list (<dl>) — semantically correct for label/value pairs and
// what screen readers announce coherently. Null percentiles for empty-run
// safety (SpecFlow gap #1).

interface Props {
  metrics: CycleCountMetrics;
}

function fmtMs(v: number | null): string {
  if (v === null) return '—';
  return `${v.toFixed(1)} ms`;
}

export function MetricsPanel({ metrics }: Props) {
  return (
    <section
      aria-labelledby="cycle-count-metrics-heading"
      data-testid="cycle-count-metrics-panel"
      className="rounded-lg bg-gray-900 p-4"
    >
      <h2
        id="cycle-count-metrics-heading"
        className="text-sm font-semibold uppercase tracking-wide text-gray-300"
      >
        Run metrics
      </h2>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-200 sm:grid-cols-4">
        <div>
          <dt className="text-xs text-gray-500">Workers</dt>
          <dd className="tabular-nums">{metrics.workerCount}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Frames processed</dt>
          <dd className="tabular-nums">{metrics.framesProcessed}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Detected events</dt>
          <dd className="tabular-nums">{metrics.framesDetected}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Frames dropped</dt>
          <dd className="tabular-nums">{metrics.framesDropped}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Unique bins</dt>
          <dd className="tabular-nums">{metrics.uniqueBins}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Fast-path p50</dt>
          <dd className="tabular-nums">{fmtMs(metrics.fastPathP50Ms)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Fast-path p95</dt>
          <dd className="tabular-nums">{fmtMs(metrics.fastPathP95Ms)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">LLM p50</dt>
          <dd className="tabular-nums">{fmtMs(metrics.llmP50Ms)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">LLM p95</dt>
          <dd className="tabular-nums">{fmtMs(metrics.llmP95Ms)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">LLM completed</dt>
          <dd className="tabular-nums">{metrics.llmCompleted}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">LLM failed</dt>
          <dd className="tabular-nums">{metrics.llmFailed}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Cancelled (queued)</dt>
          <dd className="tabular-nums">{metrics.llmCancelledBeforeStart}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Cancelled (mid-call)</dt>
          <dd className="tabular-nums">{metrics.llmCancelledMidCall}</dd>
        </div>
      </dl>
      {metrics.uniqueBins === 0 && (
        <p className="mt-3 text-xs text-gray-500">No detections this run — percentiles omitted.</p>
      )}
    </section>
  );
}
